import { Switch } from '@/components/ui/switch';
import { RowGroup, RowLink } from '@/components/ui/row-link';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { plural } from '@/lib/client-withdraw';
import { Accordion } from '@/components/ui/accordion';
import { BottomSheet } from '@/components/ui/dialog';
import { useEffect, useRef, useState } from 'react';
import { confirmAction, getWebApp, haptic } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import { BroadcastResults } from '@/components/BroadcastResults';
import { Plus, Send, CheckCircle2, Clock, Loader2, Ticket, ChevronRight, Megaphone, Settings2, Shield, Forward, Gift, Repeat, Star, Store } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { MessagePreview } from '@/components/BroadcastComposerHelpers';
import { TelegramEditor } from '@/components/text-editor/TelegramEditor';
import { visibleLength } from '@/components/text-editor/serialize';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonParagraph } from '@/components/ui/skeleton';
/** Лимит подписи к медиа в Telegram. Зеркалит CAPTION_LIMIT в broadcasts/media_sender.py. */
const CAPTION_LIMIT = 1024;
import {
  SpeedSlider,
  ButtonsBuilder,
  MediaUploader,
  TestSendButton,
  BroadcastLiveView,
  inlineButtonsValid,
  type InlineButtonRows,
  type MediaValue,
} from '@/components/BroadcastControls';

type Audience =
  | 'all_clients'
  | 'active_clients'
  | 'expired_clients'
  | 'paid_clients'
  | 'unpaid_clients'
  | 'trial_no_pay';

const AUDIENCES: Audience[] = [
  'all_clients',
  'active_clients',
  'expired_clients',
  'paid_clients',
  'unpaid_clients',
  'trial_no_pay',
];

interface Broadcast {
  id: string;
  title: string;
  body: string;
  audience: string;
  status: string;
  sent_count: number;
  failed_count: number;
  total_count: number;
  message_preview: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  // Расписание. `is_recurring_active` — единственный честный признак «ещё будет
  // уходить»: у повторяющейся рассылки между запусками status='sent'.
  recurrence?: string | null;
  time_of_day?: string | null;
  weekday?: number | null;
  is_recurring_active?: boolean;
  next_fire_at?: string | null;
  fire_count?: number;
}

type MarketingSearch = { bcastDraft?: string };

export const Route = createFileRoute('/marketing')({
  // Токен заготовки рассылки. Приходит двумя путями: deeplink'ом из бота
  // («переслал пост») и переходом из розыгрыша по «Рассказать клиентам». Без
  // объявления роутер выбросил бы параметр на своём переходе, и composer открылся
  // бы пустым.
  validateSearch: (s: Record<string, unknown>): MarketingSearch => ({
    bcastDraft: typeof s.bcastDraft === 'string' ? s.bcastDraft : undefined,
  }),
  component: Marketing,
});

const FRANCHISE_BOT = import.meta.env.VITE_FRANCHISE_BOT_USERNAME || 'hexvpnmultibot';

function Marketing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [composerOpen, setComposerOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  // Рассылка из сообщения: заготовка от бота (текст+медиа), приходит по ?bcastDraft.
  // key ремаунтит ComposerSheet, чтобы initial-стейт подхватил предзаполнение.
  const [prefill, setPrefill] = useState<{
    body: string;
    media: MediaValue;
    buttons: InlineButtonRows;
    botId: string | null;
  } | null>(null);
  const [composerKey, setComposerKey] = useState(0);

  const openBlank = () => {
    haptic('light');
    setPrefill(null);
    setComposerKey((k) => k + 1);
    setComposerOpen(true);
  };

  const openFromMessage = () => {
    haptic('light');
    const url = `https://t.me/${FRANCHISE_BOT}?start=newpost`;
    const tg = getWebApp();
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url);
      // Закрываем мини-апп: иначе он висит поверх чата, и юзер не видит, что бот
      // уже написал «перешли пост» — думает, что ничего не произошло.
      tg.close?.();
    } else {
      window.open(url, '_blank');
    }
  };

  // Один бот — ведём в его отзывы напрямую, минуя список ботов. Кэш уже прогрет
  // соседними экранами, отдельного ожидания это не добавляет.
  const { data: botsForReviews } = useQuery({ queryKey: ['bots'], queryFn: () => api.bots() });
  const reviewsBotId = botsForReviews?.length === 1 ? botsForReviews[0].id : null;

  const { data: broadcasts, isPending: broadcastsPending } = useQuery({
    queryKey: ['broadcasts'],
    queryFn: () => api.broadcasts(),
    refetchInterval: (query) => {
      const rows = (query.state.data as Broadcast[] | undefined) ?? [];
      // если есть незавершённые — поллим каждые 5с, иначе нет
      const hasRunning = rows.some((b) => b.status === 'queued' || b.status === 'running');
      return hasRunning ? 5_000 : false;
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  // Заготовка рассылки: deeplink из бота («переслал пост») или переход из розыгрыша.
  // Читаем через роутер, а не из `window.location`: на внутреннем переходе строка
  // адреса обновляется после рендера, и сырой парсер увидел бы её пустой.
  const { bcastDraft: token } = Route.useSearch();
  // Токен, который уже забрали: он же защита от повторного открытия при перерендере,
  // поэтому `cancelled` тут не нужен и его нет намеренно.
  //
  // Порядок в `then` важен: очистка адреса меняет `token`, эффект перезапускается и
  // его cleanup срабатывает синхронно. Любая проверка «отменили?» ПОСЛЕ очистки уже
  // истинна, и composer молча не открывался. Поэтому сначала состояние, потом адрес.
  const draftTaken = useRef<string | null>(null);
  useEffect(() => {
    if (!token || draftTaken.current === token) return;
    draftTaken.current = token;
    const clearUrl = () => navigate({ to: '/marketing', search: {}, replace: true });
    api
      .getBroadcastDraft(token)
      .then((d) => {
        setPrefill({
          body: d.body || '',
          media:
            d.media_type && d.media_url
              ? { media_type: d.media_type, media_url: d.media_url }
              : null,
          buttons: d.inline_buttons ?? [],
          botId: d.bot_id ?? null,
        });
        setComposerKey((k) => k + 1);
        setComposerOpen(true);
        void clearUrl();
      })
      .catch(() => {
        toast.error('Черновик не найден или истёк');
        void clearUrl();
      });
  }, [token, navigate]);

  return (
    <>
      <div className="p-4 space-y-4 pb-28">
        <div className="flex items-center justify-between px-1 pt-2">
          <h2 className="font-display text-[22px] font-medium">{t('Marketing')}</h2>
          <button
            type="button"
            onClick={openBlank}
            aria-label="Создать рассылку"
            className="w-[42px] h-[42px] rounded-[14px] bg-primary text-primary-foreground flex items-center justify-center active:scale-90 transition-transform"
          >
            <Plus className="w-5 h-5" strokeWidth={2.4} />
          </button>
        </div>

        <RowGroup>
          <RowLink to="/vouchers" icon={<Ticket />} label="Ваучеры" hint="Коды на подписку для блогеров и акций" />
          <RowLink to="/giveaways" icon={<Gift />} label="Розыгрыши" hint="Подписка за подписку на канал или друзей" />
          <RowLink to="/mirrors" icon={<Store />} label="Свой VPN" hint="Клиенты открывают магазины, вы получаете долю" />
          {/* В отзывы напрямую, когда бот один: список ботов был лишним шагом. */}
          <RowLink
            to={reviewsBotId ? `/bot-reviews/${reviewsBotId}` : '/bots'}
            icon={<Star />}
            label="Отзывы клиентов"
            hint="Ответы, свои отзывы, награда"
          />
        </RowGroup>

        <TrialCard />

        <div className="eyebrow px-1 pt-1">Рассылки</div>

        <button
          type="button"
          onClick={openFromMessage}
          className="w-full surface-card rounded-[18px] p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform text-left"
        >
          <div className="w-10 h-10 rounded-[12px] bg-white/[0.07] text-muted-foreground flex items-center justify-center shrink-0">
            <Forward className="w-5 h-5" strokeWidth={1.9} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[14px]">Есть готовый пост? Перешлите его боту</div>
            <div className="text-[11.5px] text-dim mt-0.5 leading-snug">
              Соберём рассылку из сообщения: с форматированием, премиум-эмодзи и медиа
            </div>
          </div>
          <ChevronRight className="w-[18px] h-[18px] text-dim shrink-0" />
        </button>

        {broadcastsPending ? (
          // Пустой список во время загрузки читался как «рассылок нет» — партнёр
          // с историей в сотню рассылок видел приглашение создать первую.
          <BroadcastsSkeleton />
        ) : !broadcasts || broadcasts.length === 0 ? (
          <div className="surface-card rounded-[24px] py-9 px-6 text-center">
            <div className="w-14 h-14 rounded-[17px] bg-white/[0.07] text-muted-foreground flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-[27px] h-[27px]" strokeWidth={1.6} />
            </div>
            <div className="font-display font-semibold text-base">{t('Marketing empty')}</div>
            <p className="text-[12.5px] text-dim mt-1.5 leading-relaxed">{t('Marketing empty desc')}</p>
            <button
              type="button"
              onClick={() => {
                haptic('light');
                setComposerOpen(true);
              }}
              className="mt-[18px] bg-primary text-primary-foreground px-[22px] py-3 rounded-[14px] text-sm font-semibold inline-flex items-center gap-2 active:scale-[0.97] transition-transform"
            >
              <Plus className="w-4 h-4" strokeWidth={2.2} />
              Создать рассылку
            </button>
          </div>
        ) : (
          broadcasts.map((b) => (
            <BroadcastCard key={b.id} bc={b} onClick={() => setDetailId(b.id)} />
          ))
        )}
      </div>

      <ComposerSheet
        key={composerKey}
        open={composerOpen}
        onClose={() => {
          setComposerOpen(false);
          setPrefill(null);
        }}
        initialBody={prefill?.body ?? ''}
        initialMedia={prefill?.media ?? null}
        initialButtons={prefill?.buttons ?? []}
        initialBotId={prefill?.botId ?? null}
      />
      <BroadcastDetailSheet
        row={(broadcasts ?? []).find((b) => b.id === detailId) ?? null}
        onClose={() => setDetailId(null)}
      />
    </>
  );
}

/** Пробные периоды: тумблеры на всех ботов партнёра (флаги живут на партнёре). У «На
 * срок» и «Навсегда» свои тумблеры (заявка #160): партнёры убирали один, не трогая
 * другой. Зеркало `partner-site/components/bot-settings/TrialCard.tsx`. */
function TrialCard() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['trial-settings'],
    queryFn: () => api.trialSettings(),
    staleTime: 60_000,
  });
  const mut = useMutation({
    mutationFn: (body: { enabled?: boolean; lifetime_enabled?: boolean; whitelist_auto?: boolean }) =>
      api.updateTrialSettings(body),
    onSuccess: (fresh, body) => {
      qc.setQueryData(['trial-settings'], fresh);
      haptic('success');
      if (body.whitelist_auto !== undefined) {
        toast.success(
          fresh.whitelistAuto ? 'Белые списки будут выдаваться' : 'Выдача выключена',
        );
      } else if (body.lifetime_enabled !== undefined) {
        toast.success(
          fresh.lifetimeEnabled ? 'Пробник «Навсегда» включён' : 'Пробник «Навсегда» выключен',
        );
      } else {
        const name = fresh.lifetimePlanAvailable ? 'Пробник на срок' : 'Пробный период';
        toast.success(fresh.enabled ? `${name} включён` : `${name} выключен`);
      }
    },
    onError: () => {
      haptic('error');
      toast.error('Не сохранилось');
    },
  });

  if (!data) return null;
  const enabled = data.enabled;
  const lifetime = data.lifetimePlanAvailable;

  return (
    <div className="surface-card rounded-[20px] p-4 space-y-3">
      <div className="flex items-center gap-3.5">
        <div className="w-11 h-11 rounded-[13px] bg-white/[0.07] text-muted-foreground flex items-center justify-center shrink-0">
          <Gift className="w-[21px] h-[21px]" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px]">Пробный период</div>
          {lifetime && enabled && data.lifetimeEnabled && (
            <div className="text-xs text-dim mt-0.5 leading-snug">
              Клиент берёт один пробник из двух: тот, которым будет пользоваться дальше.
            </div>
          )}
        </div>
      </div>

      <TrialToggle
        title={lifetime ? 'На срок' : 'Пробник'}
        note={
          data.planAvailable
            ? `Бесплатно ${data.days} ${plural(data.days, 'день', 'дня', 'дней')}, ${data.devices} устр. Один раз на клиента.`
            : 'Пробного тарифа сейчас нет у провайдера.'
        }
        checked={enabled}
        disabled={mut.isPending || !data.planAvailable}
        onChange={(v) => mut.mutate({ enabled: v })}
      />
      {lifetime && (
        <TrialToggle
          title="«Навсегда»"
          note={`Бесплатно ${data.lifetimeDays} ${plural(data.lifetimeDays, 'день', 'дня', 'дней')}, ${data.lifetimeDevices} устр.`}
          checked={data.lifetimeEnabled}
          disabled={mut.isPending}
          onChange={(v) => mut.mutate({ lifetime_enabled: v })}
        />
      )}

      <div className="text-[11.5px] text-dim leading-relaxed">
        Настройка общая для всех ваших ботов. Выключение убирает пробник из кнопки
        «Попробовать бесплатно», уже выданные пробные подписки продолжают работать до
        конца срока.
        {data.claimedTotal > 0 && ` Активаций за всё время: ${data.claimedTotal}.`}
      </div>

      {/* Белые списки триальщикам: запас — купленные заранее ваучеры на спецтариф,
          поэтому уйти в минус тут нельзя. Выдаются только к пробнику на срок. */}
      {enabled && (
        <div className="pt-3 border-t border-white/5 space-y-2">
          <label className="flex min-h-[44px] items-center justify-between gap-3 cursor-pointer">
            <span className="text-[13px] font-semibold">Белые списки триальщикам</span>
            <Switch
              checked={data.whitelistAuto}
              disabled={mut.isPending}
              onCheckedChange={(v) => mut.mutate({ whitelist_auto: v })}
            />
          </label>
          <p className="text-[11.5px] text-dim leading-relaxed">
            Каждому, кто активирует пробный период на срок, автоматически выдаём один
            ваучер на «Белые списки». Тратятся ваучеры, купленные заранее: осталось{' '}
            <b className="text-foreground">{data.whitelistLeft}</b>. Когда кончатся, выдача
            просто прекратится, а пробный период продолжит работать.
          </p>
          <Link to="/vouchers" className="inline-block text-[12px] font-semibold text-primary-light">
            Купить ещё →
          </Link>
        </div>
      )}
    </div>
  );
}

function TrialToggle({
  title,
  note,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  note: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-3 cursor-pointer py-1">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold">{title}</div>
        <div className="text-[11.5px] text-dim mt-0.5 leading-snug">{note}</div>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} className="mt-0.5" />
    </label>
  );
}

/** Расписание словами: «Каждый день в 10:00 · следующая 16.08 в 10:00». Время — МСК
 *  (так его задают в композере, так же и показываем). */
function scheduleLine(bc: Broadcast, locale: string): string | null {
  if (!bc.is_recurring_active) return null;
  const every =
    bc.recurrence === 'daily'
      ? `Каждый день${bc.time_of_day ? ` в ${bc.time_of_day}` : ''}`
      : bc.recurrence === 'weekly'
        ? `Каждую неделю${bc.time_of_day ? ` в ${bc.time_of_day}` : ''}`
        : 'Запланирована';
  if (!bc.next_fire_at) return `${every} (МСК)`;
  const next = new Date(bc.next_fire_at).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${every} (МСК) · следующая ${next}`;
}

function BroadcastDetailSheet({
  row,
  onClose,
}: {
  row: Broadcast | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en' : 'ru';
  const cancelMut = useMutation({
    mutationFn: (bid: string) => api.cancelBroadcast(bid),
    onSuccess: () => {
      haptic('success');
      toast.success('Рассылка остановлена');
      void qc.invalidateQueries({ queryKey: ['broadcasts'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Не удалось остановить'),
  });

  // Остановить можно и уже отправленную: если расписание живо, завтра она уйдёт снова.
  const canStop =
    row !== null &&
    (row.status === 'running' || row.status === 'queued' || !!row.is_recurring_active);
  const schedule = row ? scheduleLine(row, locale) : null;
  const finished = row !== null && ['sent', 'cancelled', 'failed'].includes(row.status);

  return (
    <BottomSheet open={row !== null} onClose={onClose} title={finished ? 'Результат рассылки' : 'Ход рассылки'}>
      {/* Отправка закончилась: сначала итог (нажатия, покупки), ход отправки ниже. */}
      {row && finished && (
        <div className="mb-4 border-b border-white/5 pb-4">
          <BroadcastResults broadcastId={row.id} recurring={Boolean(row.recurrence)} />
        </div>
      )}
      {row && finished ? (
        <details>
          <summary className="cursor-pointer text-[13px] text-muted-foreground">Журнал отправки</summary>
          <div className="mt-3">
            <BroadcastLiveView broadcastId={row.id} fetchLive={(bid) => api.broadcastLive(bid)} />
          </div>
        </details>
      ) : row ? (
        <BroadcastLiveView broadcastId={row.id} fetchLive={(bid) => api.broadcastLive(bid)} />
      ) : null}
      {schedule && (
        <div className="mt-3 surface-inset rounded-[13px] px-3.5 py-2.5 text-[12px] text-amber leading-snug">
          {schedule}
          {row?.fire_count ? ` · отправлена ${row.fire_count} раз` : ''}
        </div>
      )}
      {canStop && row && (
        <button
          type="button"
          disabled={cancelMut.isPending}
          onClick={() => cancelMut.mutate(row.id)}
          className="mt-3 w-full py-3 rounded-[14px] bg-[rgba(248,113,113,0.14)] text-danger font-semibold text-sm disabled:opacity-50"
        >
          {cancelMut.isPending ? 'Останавливаем…' : 'Остановить рассылку'}
        </button>
      )}
    </BottomSheet>
  );
}

function BroadcastCard({ bc, onClick }: { bc: Broadcast; onClick?: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en' : 'ru';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left surface-card rounded-[18px] p-4 space-y-2 active:scale-[0.99] transition-transform"
    >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-[14.5px] truncate">{bc.title}</div>
            <div className="text-[11.5px] text-dim mt-0.5">{audienceLabel(bc.audience, t)}</div>
          </div>
          <BroadcastBadge status={bc.status} />
        </div>
        <div className="text-sm text-muted-foreground line-clamp-2 whitespace-pre-wrap [overflow-wrap:anywhere]">
          {plainPreview(bc.message_preview || bc.body)}
        </div>
        <div className="text-[12px] text-dim">
          {bc.status === 'sent' && (
            <>
              {t('Bc status sent fmt', { sent: bc.sent_count, total: bc.total_count })}
              {bc.failed_count > 0 && t('Bc status sent errors', { count: bc.failed_count })}
              {' · '}
              {new Date(bc.created_at).toLocaleDateString(locale)}
            </>
          )}
          {bc.status === 'running' && (
            <>{t('Bc status running fmt', { sent: bc.sent_count, total: bc.total_count })}</>
          )}
          {bc.status === 'queued' && (
            <>{t('Bc status queued fmt', { time: new Date(bc.created_at).toLocaleTimeString(locale) })}</>
          )}
          {bc.status === 'failed' && <>{t('Bc status failed')}</>}
        </div>
        {bc.is_recurring_active && (
          <div className="text-[12px] text-muted-foreground flex items-center gap-1">
            <Repeat className="w-3 h-3 shrink-0" />
            {scheduleLine(bc, locale)}
          </div>
        )}
    </button>
  );
}

const _KNOWN_AUDIENCES = new Set([
  'all_clients',
  'active_clients',
  'expired_clients',
  'paid_clients',
  'unpaid_clients',
  'trial_no_pay',
]);

function audienceLabel(a: string, t: (k: string) => string) {
  return t(_KNOWN_AUDIENCES.has(a) ? `Bc audience ${a}` : 'Bc audience all_clients');
}

function BroadcastBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const cfg: Record<string, { cls: string; icon: React.ReactNode; label: string }> = {
    sent: { cls: 'bg-white/[0.06] text-muted-foreground', icon: <CheckCircle2 className="w-3 h-3" />, label: t('Bc badge sent') },
    running: { cls: 'bg-white/[0.07] text-primary-light', icon: <Loader2 className="w-3 h-3 animate-spin" />, label: t('Bc badge sending') },
    queued: { cls: 'bg-[rgba(240,176,78,0.15)] text-amber', icon: <Clock className="w-3 h-3" />, label: t('Bc badge queued') },
    failed: { cls: 'bg-[rgba(248,113,113,0.14)] text-danger', icon: <></>, label: t('Bc badge failed') },
    // Отменённая рассылка (`PartnerBroadcastStatus.CANCELLED`) показывалась словом «CANCELLED».
    cancelled: { cls: 'bg-white/10 text-muted-foreground', icon: <></>, label: t('Bc badge cancelled') },
  };
  const c = cfg[status] ?? { cls: 'bg-white/10 text-muted-foreground', icon: <></>, label: status };
  return (
    <span className={`flex items-center gap-1 text-[12px] font-medium px-2.5 py-1 rounded-full shrink-0 ${c.cls}`}>
      {c.icon}
      {c.label}
    </span>
  );
}

function ComposerSheet({
  open,
  onClose,
  initialBody = '',
  initialMedia = null,
  initialButtons = [],
  initialBotId = null,
}: {
  open: boolean;
  onClose: () => void;
  initialBody?: string;
  initialMedia?: MediaValue;
  initialButtons?: InlineButtonRows;
  initialBotId?: string | null;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [body, setBody] = useState(initialBody);
  const [audience, setAudience] = useState<Audience>('all_clients');
  // Block A.8: scheduled/recurring broadcasts
  type Recurrence = 'none' | 'once' | 'daily' | 'weekly';
  const [recurrence, setRecurrence] = useState<Recurrence>('none');
  const [scheduledAt, setScheduledAt] = useState(''); // ISO datetime для once
  const [timeOfDay, setTimeOfDay] = useState(''); // HH:MM для daily/weekly
  const [weekday, setWeekday] = useState<number>(0); // 0..6 для weekly
  // Block D.3: защита от форварда
  const [protectContent, setProtectContent] = useState(false);
  // Native TG message effect (Bot API 7.2+) — анимированная реакция при доставке.
  const [messageEffect, setMessageEffect] = useState<string | null>(null);
  // Per-bot scope. null = "Все боты" партнёра.
  const [targetBotId, setTargetBotId] = useState<string | null>(initialBotId);
  // Ф3/Ф4/Ф7: скорость, кнопки, медиа.
  const [ratePerSecond, setRatePerSecond] = useState(25);
  const [inlineButtons, setInlineButtons] = useState<InlineButtonRows>(initialButtons);
  const [trackLinks, setTrackLinks] = useState(false);
  // Галочка нужна, только если есть ссылка не в Telegram: t.me открывается сразу в
  // Telegram, через наш редирект её не гоняем (заявка #203).
  const hasCountableLinks = inlineButtons
    .flat()
    .some(
      (b) =>
        b.kind !== 'action' &&
        /^https?:\/\//i.test(b.url ?? '') &&
        !/^https?:\/\/(www\.)?(t\.me|telegram\.(me|dog))(\/|$)/i.test(b.url ?? ''),
    );
  const [media, setMedia] = useState<MediaValue>(initialMedia);
  // Файл ещё грузится: отправка без него ушла бы всем клиентам голым текстом.
  const [mediaUploading, setMediaUploading] = useState(false);
  const sendBusyRef = useRef(false);

  // Список ботов партнёра для селектора. Скрываем селектор если только 1 бот.
  const { data: bots = [] } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    enabled: open,
    staleTime: 60 * 1000,
  });
  const showBotSelector = bots.length > 1;

  // Тест уходит через конкретного бота, поэтому его надо знать наверняка. Раньше при
  // «во все боты» бэк брал первого попавшегося из БД (без сортировки), и у партнёра с
  // несколькими ботами тест то приходил, то нет — в зависимости от того, в каком из них
  // партнёр нажимал /start. С единственным ботом выбирать нечего, подставляем его сами.
  // Остановленный бот для теста не годится: бэк требует статус ACTION и вернёт тот же
  // 409, только с другим текстом. Подставляем единственного АКТИВНОГО.
  const testableBots = bots.filter((b) => b.status === 'active');
  const testBotId = targetBotId ?? (testableBots.length === 1 ? testableBots[0].id : null);

  // Block 5: список переменных для chip-helper. Грузится один раз при открытии.
  const { data: templateVars = [] } = useQuery({
    queryKey: ['broadcast-template-vars'],
    queryFn: () => api.broadcastTemplateVars(),
    enabled: open,
    staleTime: 60 * 60 * 1000, // 1ч — список редко меняется
  });

  // Действия для кнопок (тарифы, профиль, промокод…) — реестр отдаёт бэк.
  const { data: buttonActions = [] } = useQuery({
    queryKey: ['broadcast-button-actions'],
    queryFn: () => api.broadcastButtonActions(),
    enabled: open,
    staleTime: 60 * 60 * 1000,
  });

  // Размер аудитории для подтверждения считаем заранее: ждать сеть в момент нажатия
  // значило бы кнопку, которая «не реагирует».
  const audienceSize = useQuery({
    queryKey: ['bc-audience-size', audience, targetBotId],
    queryFn: () => api.broadcastAudienceSize(audience, targetBotId),
    enabled: open,
    staleTime: 30_000,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.createBroadcast({
        body,
        audience,
        recurrence: recurrence === 'none' ? null : recurrence,
        scheduled_at: recurrence === 'once' ? scheduledAt : null,
        time_of_day: recurrence === 'daily' || recurrence === 'weekly' ? timeOfDay : null,
        weekday: recurrence === 'weekly' ? weekday : null,
        protect_content: protectContent,
        reaction_emojis: null,
        message_effect_id: messageEffect,
        bot_id: targetBotId,
        rate_per_second: ratePerSecond,
        inline_buttons: inlineButtons.length > 0 ? inlineButtons : null,
        media_type: media?.media_type ?? null,
        media_url: media?.media_url ?? null,
        track_links: hasCountableLinks && trackLinks,
      }),
    onSuccess: (created) => {
      qc.setQueryData<Broadcast[]>(['broadcasts'], (prev) => [created, ...(prev || [])]);
      haptic('success');
      toast.success(t('Bc toast queued'));
      setBody('');
      setAudience('all_clients');
      setInlineButtons([]);
      setMedia(null);
      onClose();
    },
    onError: (e: Error) => {
      haptic('error');
      toast.error(t('Bc toast failed', { err: e.message }));
    },
    onSettled: () => {
      // Бэк-воркер обработает рассылку асинхронно — нужен принудительный refetch
      // чтобы юзер увидел реальный статус (running/sent/failed), а не optimistic.
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
    },
  });

  // Кнопки с пустым/бессхемным URL бэк отклоняет (422). Гейтим отправку, чтобы
  // партнёр сначала починил кнопку, а не упирался в непонятную ошибку.
  const buttonsOk = inlineButtonsValid(inlineButtons);
  // Расписание: у бэка time_of_day обязан быть HH:MM (pattern), scheduled_at — непустой.
  // Пустое поле при выбранном режиме → 422; гейтим здесь.
  const scheduleOk =
    recurrence === 'none'
      ? true
      : recurrence === 'once'
        ? scheduledAt.trim() !== ''
        : /^\d{2}:\d{2}$/.test(timeOfDay); // daily / weekly
  // С медиа текст уходит подписью, а у неё лимит 1024 против 4096 у обычного
  // сообщения. Пока этого не проверяли, Telegram отбивал КАЖДОГО получателя, и
  // партнёр видел «0 доставлено из 1059» без объяснения.
  const textLimit = media ? CAPTION_LIMIT : 4000;
  const textLen = visibleLength(body);
  const textOk = textLen <= textLimit;
  const canSend = body.trim().length > 2 && buttonsOk && scheduleOk && textOk && !mediaUploading;

  // Рассылку не отозвать, поэтому перед отправкой называем аудиторию и число
  // получателей. Замок держит кнопку, пока висит попап и считается аудитория.
  const onSend = async () => {
    if (!canSend || isPending || sendBusyRef.current) return;
    sendBusyRef.current = true;
    // Не посчиталось или ещё считается: спросим без числа, отправку это не держит.
    const count = audienceSize.data?.count ?? null;
    const who = audienceLabel(audience, t);
    const ok = await confirmAction(
      count === null
        ? `Аудитория: ${who}. Отправленную рассылку не отозвать.`
        : `Аудитория: ${who}. Получателей: ${count.toLocaleString('ru')}. Отправленную рассылку не отозвать.`,
      { title: 'Отправить рассылку?', okText: 'Отправить', destructive: false },
    );
    sendBusyRef.current = false;
    if (ok) mutate();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={t('Bc composer title')}>
      <div className="space-y-3">
        {showBotSelector && (
          <Field label="В какой бот отправить">
            <select
              value={targetBotId ?? ''}
              onChange={(e) => setTargetBotId(e.target.value || null)}
              className="w-full surface-inset rounded-[13px] px-4 py-3 text-sm focus:outline-none focus:border-white/30 appearance-none cursor-pointer"
            >
              <option value="">Во все боты партнёра</option>
              {bots.map((b) => (
                <option key={b.id} value={b.id}>
                  @{b.username} · {b.brandName}
                </option>
              ))}
            </select>
          </Field>
        )}
        <MediaUploader
          value={media}
          onChange={setMedia}
          onBusyChange={setMediaUploading}
          uploadFn={(file) => api.uploadBroadcastMedia(file)}
        />
        <Field label={t('Bc field text')}>
          {/* WYSIWYG-редактор: жирный/курсив/ссылки + премиум-эмодзи (⚡) +
              переменные ($). Заголовок отдельным полем убрали — форматируй в тексте. */}
          <TelegramEditor
            value={body}
            onChange={setBody}
            maxLen={textLimit}
            dollarVars={templateVars}
          />
          {!textOk && (
            <p className="text-[12px] text-danger mt-1.5 leading-snug">
              С картинкой или видео Telegram разрешает не больше {CAPTION_LIMIT} символов
              (сейчас {textLen}). Сократите текст или уберите медиа, иначе рассылка не
              уйдёт ни одному клиенту.
            </p>
          )}
        </Field>
        {body.trim() && <MessagePreview body={body} vars={templateVars} />}
        <ButtonsBuilder value={inlineButtons} onChange={setInlineButtons} actions={buttonActions} />
        {hasCountableLinks && (
          <label className="flex items-center justify-between gap-3 cursor-pointer py-1 px-1">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Считать переходы по ссылкам</div>
              <div className="text-[12px] text-muted-foreground leading-snug">
                Ссылка откроется через наш адрес, и в результате будет видно, сколько людей перешло
              </div>
            </div>
            <Switch checked={trackLinks} onCheckedChange={setTrackLinks} />
          </label>
        )}
        <Field label={t('Bc field audience')}>
          <div className="grid grid-cols-1 gap-2">
            {AUDIENCES.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAudience(a)}
                className={`px-3 py-2.5 rounded-xl text-sm font-semibold text-left transition-colors duration-100 ${
                  audience === a ? 'bg-white/[0.12] text-foreground' : 'bg-white/8 text-muted-foreground'
                }`}
              >
                {audienceLabel(a, t)}
              </button>
            ))}
          </div>
        </Field>

      </div>

      {/* Дополнительные параметры — свёрнуты по дефолту: убирает визуальный шум.
          Без них рассылка работает «отправить сейчас всем выбранным». */}
      <div className="mt-3">
        <Accordion title="Дополнительные параметры" icon={<Settings2 className="w-4 h-4" />} defaultOpen={false}>
          <div className="space-y-4">
            {/* Ф3 скорость */}
            <SpeedSlider value={ratePerSecond} onChange={setRatePerSecond} />

            {/* A.8 расписание. 2×2 — длинные подписи («Каждую неделю») влезают. Время — МСК. */}
            <Field label={t('Bc field schedule')}>
              <div className="grid grid-cols-2 gap-1.5">
                {(['none', 'once', 'daily', 'weekly'] as Recurrence[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRecurrence(r)}
                    className={`px-2 py-2.5 rounded-lg text-xs font-semibold transition-colors duration-100 ${
                      recurrence === r
                        ? 'bg-white/[0.12] text-foreground'
                        : 'bg-white/8 text-muted-foreground'
                    }`}
                  >
                    {t(`Bc rec_${r}`)}
                  </button>
                ))}
              </div>
              <p className="text-[12px] text-muted-foreground mt-1.5 leading-snug">
                {t('Bc rec hint')}
              </p>
              {recurrence === 'once' && (
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="mt-2 w-full surface-inset rounded-[13px] px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
                />
              )}
              {(recurrence === 'daily' || recurrence === 'weekly') && (
                <input
                  type="time"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                  className="mt-2 w-full surface-inset rounded-[13px] px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
                />
              )}
              {recurrence === 'weekly' && (
                <>
                  <div className="text-[12px] text-muted-foreground mt-2 mb-1">
                    {t('Bc field weekday')}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d, i) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setWeekday(i)}
                        className={`py-1.5 rounded-lg text-[12px] font-semibold ${
                          weekday === i
                            ? 'bg-white/[0.12] text-foreground'
                            : 'bg-white/8 text-muted-foreground'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </Field>

            {/* D.3 protect_content toggle */}
            <label className="flex items-center justify-between gap-3 cursor-pointer py-2 px-1">
              <div>
                <div className="text-sm font-semibold flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-primary-light" strokeWidth={1.8} />
                  Защитить от форварда
                </div>
                <div className="text-[12px] text-muted-foreground">
                  Клиент не сможет переслать или копировать сообщение
                </div>
              </div>
              <Switch checked={protectContent} onCheckedChange={setProtectContent} />
            </label>

            {/* Анимированный эффект при доставке — native Telegram feature.
                Клиент получает сообщение → играется floating-анимация эмодзи. */}
            <Field label="Анимация при доставке">
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: null, label: '—', sub: 'без эффекта' },
                  { id: '5104841245755282124', label: '🔥', sub: 'Огонь' },
                  { id: '5159385139981059251', label: '❤️', sub: 'Сердце' },
                  { id: '5046509860389126442', label: '🎉', sub: 'Праздник' },
                  { id: '5107584321108051014', label: '👍', sub: 'Класс' },
                  { id: '5104858069142078462', label: '👎', sub: 'Дизлайк' },
                  { id: '5046589136895476101', label: '💩', sub: 'Кака' },
                ].map((opt) => {
                  const active = messageEffect === opt.id;
                  return (
                    <button
                      key={opt.id ?? 'none'}
                      type="button"
                      onClick={() => setMessageEffect(opt.id)}
                      className={`py-3 rounded-xl flex flex-col items-center gap-1 transition-colors duration-100 ${
                        active
                          ? 'bg-white/[0.07] border border-white/30 scale-[1.02]'
                          : 'bg-white/[0.04] border border-white/5 opacity-70'
                      }`}
                    >
                      <span className="text-2xl">{opt.label}</span>
                      <span className="text-[11.5px] text-muted-foreground">{opt.sub}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[12px] text-muted-foreground">
                Эмодзи проиграется анимацией, когда клиент откроет сообщение. В свежих
                версиях Telegram, на старых просто не появится.
              </p>
            </Field>
          </div>
        </Accordion>
      </div>

      <div className="mt-3 space-y-2">
        <TestSendButton
          disabled={!canSend || !testBotId}
          hint={
            !testBotId && canSend
              ? testableBots.length === 0
                ? 'Нет активного бота: тест отправить не через кого.'
                : 'Выберите бота выше: тест уходит из него, чтобы вы увидели письмо глазами клиента.'
              : undefined
          }
          onSend={() =>
            api.testBroadcast({
              body,
              bot_id: testBotId,
              media_type: media?.media_type ?? null,
              media_url: media?.media_url ?? null,
              inline_buttons: inlineButtons.length > 0 ? inlineButtons : null,
              protect_content: protectContent,
              message_effect_id: messageEffect,
            })
          }
        />
        <button
          type="button"
          disabled={!canSend || isPending}
          onClick={() => void onSend()}
          className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
        >
          {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {t('Bc send')}
        </button>
      </div>
      {mediaUploading && (
        <p className="text-[12px] text-muted-foreground text-center">
          Файл ещё загружается. Отправить можно будет, когда он появится в превью.
        </p>
      )}
      {body.trim().length > 2 && !buttonsOk && (
        <p className="text-[12px] text-danger text-center">
          У кнопки пустой текст или ссылка без https://. Поправьте или удалите её.
        </p>
      )}
      <p className="text-[12px] text-muted-foreground text-center">
        {t('Bc throttle hint')}
      </p>
    </BottomSheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold">{label}</label>
      {children}
    </div>
  );
}

/** Карточка рассылки: заголовок с аудиторией, бейдж статуса, две строки текста. */
function BroadcastsSkeleton() {
  return (
    <>
      {[0, 1].map((i) => (
        <SkeletonCard key={i} className="rounded-[18px] p-4 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonLine className="w-2/5" />
              <SkeletonLine size="xs" className="w-1/4" />
            </div>
            <Skeleton className="h-[19px] w-16 rounded-full shrink-0" />
          </div>
          <SkeletonParagraph lines={2} size="sm" />
          <SkeletonLine size="xs" className="w-1/3" />
        </SkeletonCard>
      ))}
    </>
  );
}

/**
 * Превью текста рассылки без разметки. Тело хранится в Telegram-HTML, и карточка
 * показывала его как есть: «<b>С Новым годом!</b>», код премиум-эмодзи. Так же
 * чистит превью сайт (`partner-site`, `broadcasts.index.tsx`).
 */
function plainPreview(html: string): string {
  const noEmojiCode = html.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  const doc = new DOMParser().parseFromString(noEmojiCode, 'text/html');
  return (doc.body.textContent ?? '').trim();
}
